import { useState, useRef, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box, Fab, Paper, Typography, IconButton, TextField, Stack,
  Avatar, CircularProgress, Slide, Divider, Tooltip, Collapse,
  Button, Chip, Grow,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const PANEL_WIDTH  = 360;
const PANEL_HEIGHT = 520;
const TAB_WIDTH    = 36;
const MAX_HISTORY  = 10;

/* ─── Saludo según hora ───────────────────────────────────────────────────── */
const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 6  && h < 12) return "Buenos días";
  if (h >= 12 && h < 20) return "Buenas tardes";
  return "Buenas noches";
};

const getUserName = () => {
  try {
    const d = JSON.parse(localStorage.getItem("siam-user-data"));
    return d?.nombre || d?.name || d?.usuario || d?.email || null;
  } catch { return null; }
};

/* ─── Sugerencias contextuales por ruta ──────────────────────────────────── */
const ROUTE_SUGGESTIONS = {
  "/procesos/general": [
    "Buscá la caja 100",
    "Cajas del cliente OSDE",
    "Cambiá al proceso de Retiro de Cajas",
    "Buscá cajas alojadas",
  ],
  "/procesos/general_contenidos": [
    "Buscá el contenido 200",
    "Contenidos del cliente OSDE",
    "Cambiá al proceso de Retiro de Contenido Parcial",
    "Buscá cajas alojadas",
  ],
  "/digitalizacion/admin": [
    "Buscá la caja 100",
    "Digitalizaciones del cliente OSDE",
    "Buscá cajas alojadas",
    "Buscá por referencia ABC",
  ],
  "/dashboard": [
    "¿Cuántas cajas tengo alojadas este mes?",
    "¿Cómo busco un contenido específico?",
    "Explicame el resumen de movimientos",
  ],
  "/cajas_genericas/alojamiento": [
    "¿Cómo alojar una caja nueva?",
    "¿Qué información necesito para crear una caja?",
    "¿Qué estados puede tener una caja?",
  ],
  "/cajas_genericas/asignacion": [
    "¿Cómo asigno contenidos a una caja?",
    "¿Puedo asignar múltiples contenidos a la vez?",
  ],
  "/cajas_genericas/contenidos": [
    "¿Cómo agrego un contenido nuevo?",
    "¿Qué metadatos puedo configurar?",
    "¿Cómo busco un contenido por número?",
  ],
  "/cajas_genericas/ubicacion": [
    "¿Cómo ubico una caja en un estante?",
    "¿Qué pasa si la ubicación está ocupada?",
  ],
  "/ubicaciones/admin": [
    "Mostrá las ubicaciones disponibles",
    "Mostrá las ubicaciones ocupadas",
    "Buscá el sector 5 módulo 3",
    "Mostrá las ubicaciones del depósito central",
  ],
  "/ubicaciones/estantes": [
    "¿Cómo configuro un estante nuevo?",
    "¿Cómo organizo los niveles de un estante?",
  ],
  "/comprobantes/carga": [
    "¿Cómo genero un comprobante de retiro?",
    "¿Qué tipos de comprobantes existen?",
    "¿Cómo asocio una caja a un comprobante?",
  ],
  "/comprobantes/consulta": [
    "¿Cómo filtro comprobantes por fecha?",
    "¿Cómo descargo un comprobante en PDF?",
  ],
  "/comprobantes/resumen_cuenta": [
    "¿Qué muestra el resumen de cuenta?",
    "¿Cómo veo el saldo pendiente de un cliente?",
  ],
  "/comprobantes/servicios_guarda": [
    "Generá comprobante para OSDE desde el 01/01/2023 hasta hoy",
    "Generá comprobante para todos los clientes este mes",
    "Generá comprobante para OSDE y HSBC desde 01/08/2023 hasta hoy",
  ],
  "/movimientos/historico": [
    "¿Cómo filtro movimientos por cliente?",
    "¿Qué tipos de movimiento existen?",
  ],
  "/digitalizacion/admin": [
    "¿Cómo digitalizo documentos de una caja?",
    "¿Cómo asocio imágenes a un contenido?",
  ],
  "/importacion/contenidos": [
    "¿Qué formato necesita el archivo de importación?",
    "¿Cómo manejo errores en la importación?",
  ],
  "/ubicacion_etiquetas/proceso": [
    "¿Cómo imprimo etiquetas para varias cajas?",
    "¿Qué información incluye la etiqueta?",
  ],
  "/estadisticas/admin": [
    "¿Qué mide el contador de consultas?",
    "¿Cómo veo las estadísticas de otro usuario?",
  ],
  "/cuentas_personas/admin_juridicas": [
    "¿Cómo creo una cuenta nueva?",
    "¿Cómo edito los datos de un cliente?",
  ],
  "/cuentas/ficha": [
    "¿Qué información contiene la ficha de cuenta?",
    "¿Cómo agrego un servicio a una cuenta?",
  ],
  "/cuentas_servicios/admin": [
    "¿Cómo asigno un servicio a una cuenta?",
    "¿Cómo configuro el precio de un servicio?",
  ],
  "/usuarios/admin": [
    "¿Cómo creo un usuario nuevo?",
    "¿Cómo cambio el perfil de un usuario?",
    "¿Cómo sincronizo usuarios con Cognito?",
  ],
};

const DEFAULT_SUGGESTIONS = [
  "¿Cómo busco una caja?",
  "¿Cómo genero un comprobante?",
  "¿Cómo digitalizo documentos?",
];

const getSuggestions = (path) => {
  for (const [route, suggs] of Object.entries(ROUTE_SUGGESTIONS)) {
    if (path === route || path.startsWith(route + "/")) return suggs;
  }
  return DEFAULT_SUGGESTIONS;
};

const getContextHint = (path) => {
  const hints = {
    "/ubicaciones/admin":            "Estás en Ubicaciones. Puedo buscar por estado (disponible, ocupada, reservada), depósito, sector, módulo o estante.",
    "/cajas_genericas/alojamiento":  "Estás en Alojamiento de Cajas.",
    "/cajas_genericas/asignacion":   "Estás en Asignación de Contenidos.",
    "/procesos/general":             "Estás en Procesos General de Cajas. Puedo buscar cajas, crear nuevas o cambiar el proceso activo.",
    "/procesos/general_contenidos":  "Estás en Procesos General de Contenidos. Puedo buscar contenidos, filtrar por cliente, estado o fecha, y cambiar el proceso activo.",
    "/digitalizacion/admin":         "Estás en Digitalizaciones. Puedo buscar cajas o contenidos por ID, cliente, referencia, estado o fecha para que digitalices sus documentos.",
    "/cajas_genericas/contenidos":   "Estás en Contenidos de Cajas.",
    "/cajas_genericas/ubicacion":    "Estás en Ubicación de Cajas.",
    "/comprobantes/carga":           "Estás en Carga de Comprobantes.",
    "/comprobantes/consulta":        "Estás en Consulta de Comprobantes.",
    "/comprobantes/servicios_guarda":"Estás en Servicios de Guarda. Puedo generar comprobantes para uno o más clientes, solo decime el nombre y el período.",
    "/movimientos/historico":        "Estás en Histórico de Movimientos.",
    "/digitalizacion/admin":         "Estás en Digitalizaciones.",
    "/importacion/contenidos":       "Estás en Importación de Contenidos.",
    "/estadisticas/admin":           "Estás en Estadísticas.",
    "/usuarios/admin":               "Estás en Administración de Usuarios.",
  };
  for (const [route, hint] of Object.entries(hints)) {
    if (path === route || path.startsWith(route + "/")) return hint;
  }
  return null;
};

/* ─── Mensaje inicial dinámico ───────────────────────────────────────────── */
const buildInitialMessage = (path) => {
  const greeting    = getGreeting();
  const name        = getUserName();
  const contextHint = getContextHint(path);
  const suggestions = getSuggestions(path);

  const nameStr = name ? `, **${name}**` : "";
  const intro   = contextHint ? ` ${contextHint} ¿Te ayudo con algo de esto?` : " ¿En qué te puedo ayudar?";

  return {
    role:        "assistant",
    content:     `${greeting}${nameStr}! 👋${intro}`,
    suggestions,
  };
};

/* ─── Sanitize ───────────────────────────────────────────────────────────── */
const sanitize = (text) =>
  String(text).replace(/[\u0000-\u001F\u007F]/g, " ").slice(0, 800);

/* ─── Chips de sugerencias ───────────────────────────────────────────────── */
const SuggestionChips = ({ suggestions, onSelect, theme }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1, pl: 0.5 }}>
    {suggestions.map((s, i) => (
      <Chip
        key={i}
        label={s}
        size="small"
        icon={<AutoAwesomeIcon sx={{ fontSize: "13px !important" }} />}
        onClick={() => onSelect(s)}
        sx={{
          fontSize: "0.72rem",
          height: 26,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          cursor: "pointer",
          "& .MuiChip-icon": { color: theme.palette.primary.main },
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.16),
          },
        }}
      />
    ))}
  </Box>
);

/* ─── Chat panel compartido ───────────────────────────────────────────────── */
const ChatPanel = ({
  messages, streaming, input, setInput,
  onSend, onClear, onClose, onNavigate, onSuggestionSelect,
  inputRef, bottomRef, showClose,
}) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bubbleBg = (role) => {
    if (role === "user")
      return isDark ? alpha(theme.palette.primary.dark, 0.85) : theme.palette.primary.main;
    return isDark ? alpha(theme.palette.grey[800], 0.9) : theme.palette.grey[100];
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  };

  // Las sugerencias solo se muestran mientras no haya mensajes del usuario
  const hasUserMessages = messages.some((m) => m.role === "user");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>

      {/* Header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        px: 2, py: 1.25,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SmartToyIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="#fff">Asistente IA</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", lineHeight: 1 }}>
              Gestión Documental
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Limpiar conversación">
            <IconButton size="small" onClick={onClear} sx={{ color: "rgba(255,255,255,0.8)" }}>
              <DeleteSweepIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {showClose && (
            <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      <Divider />

      {/* Messages */}
      <Box sx={{
        flexGrow: 1, overflowY: "auto", px: 1.5, py: 1,
        display: "flex", flexDirection: "column", gap: 1,
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": { borderRadius: "4px", background: theme.palette.divider },
      }}>
        {messages.map((msg, i) => {
          const isStreaming = streaming && i === messages.length - 1 && msg.role === "assistant";
          const showSuggestions = !hasUserMessages && msg.suggestions?.length > 0 && !streaming;

          return (
            <Box key={i}>
              <Stack
                direction="row" spacing={0.75} alignItems="flex-end"
                justifyContent={msg.role === "user" ? "flex-end" : "flex-start"}
              >
                {msg.role === "assistant" && (
                  <Avatar sx={{ width: 26, height: 26, bgcolor: theme.palette.primary.main, flexShrink: 0 }}>
                    <SmartToyIcon sx={{ fontSize: 15 }} />
                  </Avatar>
                )}
                <Box sx={{
                  maxWidth: "78%",
                  bgcolor: bubbleBg(msg.role),
                  color: msg.role === "user" ? "#fff" : theme.palette.text.primary,
                  px: 1.5, py: 0.875,
                  borderRadius: msg.role === "user" ? "14px 14px 2px 14px" : "14px 14px 14px 2px",
                  fontSize: "0.84rem", lineHeight: 1.55,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  boxShadow: `0 1px 3px ${alpha("#000", 0.12)}`,
                  minHeight: isStreaming && !msg.content ? 36 : undefined,
                  display: isStreaming && !msg.content ? "flex" : "block",
                  alignItems: isStreaming && !msg.content ? "center" : undefined,
                }}>
                  {isStreaming && !msg.content ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CircularProgress size={10} thickness={5} />
                      <Typography variant="caption" color="text.secondary">Escribiendo…</Typography>
                    </Stack>
                  ) : (
                    <>
                      {/* Renderizar negrita simple (**texto**) */}
                      {msg.content.split(/(\*\*[^*]+\*\*)/).map((part, pi) =>
                        /^\*\*[^*]+\*\*$/.test(part)
                          ? <strong key={pi}>{part.slice(2, -2)}</strong>
                          : part
                      )}
                      {isStreaming && (
                        <Box component="span" sx={{
                          display: "inline-block", width: "2px", height: "1em",
                          bgcolor: "currentColor", ml: "2px", verticalAlign: "text-bottom",
                          animation: "blink 0.8s step-end infinite",
                          "@keyframes blink": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0 } },
                        }} />
                      )}
                    </>
                  )}
                </Box>
                {msg.role === "user" && (
                  <Avatar sx={{ width: 26, height: 26, bgcolor: theme.palette.grey[400], flexShrink: 0 }}>
                    <PersonIcon sx={{ fontSize: 15 }} />
                  </Avatar>
                )}
              </Stack>

              {/* Chips de sugerencias (solo en mensaje inicial) */}
              {showSuggestions && (
                <Box sx={{ pl: 4.5 }}>
                  <SuggestionChips
                    suggestions={msg.suggestions}
                    onSelect={onSuggestionSelect}
                    theme={theme}
                  />
                </Box>
              )}

              {/* Botones de navegación */}
              {!isStreaming && msg.role === "assistant" && msg.actions?.length > 0 && (
                <Box sx={{ pl: 4.5, mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {msg.actions.map((action, ai) => (
                    <Button
                      key={ai}
                      size="small"
                      variant="contained"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                      onClick={() => onNavigate(action.path)}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: "0.75rem", py: 0.4, px: 1.25 }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Box>
              )}

              {/* Opciones de página (subprocesos, etc.) */}
              {!isStreaming && msg.role === "assistant" && msg.options?.length > 0 && (
                <Box sx={{ pl: 4.5, mt: 0.75, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {msg.options.map((opt, oi) => (
                    <Chip
                      key={oi}
                      label={opt.label}
                      size="small"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent("ai:action", { detail: opt.action }));
                        onSuggestionSelect(opt.label);
                      }}
                      sx={{
                        fontSize: "0.72rem",
                        height: 26,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        color: theme.palette.primary.main,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                        cursor: "pointer",
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      <Divider />

      {/* Input */}
      <Box sx={{ px: 1.5, py: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            inputRef={inputRef}
            fullWidth multiline maxRows={3} size="small"
            placeholder="Escribí tu consulta…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={streaming}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: "0.85rem" } }}
          />
          <IconButton
            color="primary" onClick={onSend}
            disabled={!input.trim() || streaming}
            sx={{
              bgcolor: theme.palette.primary.main, color: "#fff",
              width: 38, height: 38, flexShrink: 0,
              "&:hover": { bgcolor: theme.palette.primary.dark },
              "&.Mui-disabled": { bgcolor: theme.palette.action.disabledBackground },
            }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.disabled"
          sx={{ display: "block", mt: 0.5, textAlign: "center" }}>
          Enter para enviar · Shift+Enter nueva línea
        </Typography>
      </Box>
    </Box>
  );
};

/* ─── Estado de página compartido (singleton) ────────────────────────────── */
// La página emite "ai:state" con su estado; lo almacenamos aquí para que
// tryLocalIntent pueda consultarlo sin necesidad de pasar props.
let _pageState = {};

/* ─── Intents locales para /procesos/general ─────────────────────────────── */
// Normaliza quitando acentos y pasando a minúsculas
const normalizeStr = (s) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Lee un array de un key de localStorage con formato siam-{key} → { key: [...] }
const lsGetArray = (key) => {
  try {
    const data = JSON.parse(localStorage.getItem(`siam-${key}`));
    const arr = data?.[key];
    return Array.isArray(arr) ? arr : Object.values(arr || {});
  } catch { return []; }
};

// Busca clientes en localStorage por nombre parcial
const buscarClientePorNombre = (nombre) => {
  try {
    const normNombre = normalizeStr(nombre);
    const data = JSON.parse(localStorage.getItem("siam-cuentas_personas_juridicas"));
    const todos = Array.isArray(data?.cuentas_personas_juridicas)
      ? data.cuentas_personas_juridicas
      : Object.values(data?.cuentas_personas_juridicas || {});
    return todos.filter((c) =>
      c?.razon_social && normalizeStr(c.razon_social).includes(normNombre)
    );
  } catch { return []; }
};

const PROCESOS_CAJAS = [
  { id: 1, label: "Consulta General",           patterns: [/consulta\s*general/, /\bconsulta\b/] },
  { id: 2, label: "Retiro de Cajas",            patterns: [/retiro\s+de\s+cajas/, /\bretiro\s+caja/] },
  { id: 3, label: "Retiro Definitivo",          patterns: [/retiro\s+definitivo/] },
  { id: 4, label: "Baja/Expurgue",              patterns: [/\bbaja\b/, /expurgue/] },
  { id: 5, label: "Devolucion de Cajas",        patterns: [/devolucion\s+(?:de\s+)?caja/, /\bdevolucion\b/] },
  { id: 6, label: "Ubicacion de Cajas",         patterns: [/ubicacion\s+de\s+cajas/, /\bubicacion\b(?!\s+temp)/] },
  { id: 7, label: "Ubicacion Temporal",         patterns: [/ubicacion\s+temporal/] },
  { id: 8, label: "Mudanza de Cajas",           patterns: [/mudanza/] },
];

const PROCESOS_CONTENIDOS = [
  { id: 1, label: "Consulta General",              patterns: [/consulta\s*general/, /\bconsulta\b/] },
  { id: 2, label: "Retiro de Contenido Parcial",   patterns: [/retiro\s+(?:de\s+)?contenido\s+parcial/, /retiro\s+parcial/] },
  { id: 3, label: "Retiro de Contenido Total",     patterns: [/retiro\s+(?:de\s+)?contenido\s+total/, /retiro\s+total/] },
  { id: 4, label: "Quita Total de Contenidos",     patterns: [/quita\s+total/, /quita/] },
  { id: 5, label: "Devolucion de Contenidos Parcial", patterns: [/devolucion\s+(?:de\s+)?contenido\s+parcial/, /devolucion\s+parcial/] },
  { id: 6, label: "Devolucion de Contenido Total", patterns: [/devolucion\s+(?:de\s+)?contenido\s+total/, /devolucion\s+total/] },
];

/* ─── Parser de fechas en español ───────────────────────────────────────── */
const parseFechaES = (s) => {
  if (!s) return null;
  const MESES = {
    enero:1, febrero:2, marzo:3, abril:4, mayo:5, junio:6,
    julio:7, agosto:8, septiembre:9, octubre:10, noviembre:11, diciembre:12,
  };
  const n = normalizeStr(s).trim();
  const today = new Date();
  const pad = (x) => String(x).padStart(2, "0");
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;

  if (/^hoy$|^el\s+dia\s+de\s+hoy$/.test(n)) return fmt(today);
  if (/^ayer$/.test(n)) { const d = new Date(today); d.setDate(d.getDate()-1); return fmt(d); }
  if (/inicio\s+del?\s+mes|primer\s+dia/.test(n))
    return `${today.getFullYear()}-${pad(today.getMonth()+1)}-01`;
  if (/fin\s+del?\s+mes|ultimo\s+dia/.test(n)) {
    const fin = new Date(today.getFullYear(), today.getMonth()+1, 0);
    return fmt(fin);
  }
  // "01 de agosto del 2023" / "1 de agosto de 2023"
  const mEs = n.match(/(\d{1,2})\s+de\s+(\w+)\s+(?:de(?:l)?\s+)?(\d{4})/);
  if (mEs) {
    const mes = MESES[mEs[2]];
    if (mes) return `${mEs[3]}-${pad(mes)}-${pad(mEs[1])}`;
  }
  // "agosto de 2023" / "agosto 2023" (solo mes + año → primer día del mes)
  const mMesAnio = n.match(/^(\w+)\s+(?:de(?:l)?\s+)?(\d{4})$/);
  if (mMesAnio) {
    const mes = MESES[mMesAnio[1]];
    if (mes) return `${mMesAnio[2]}-${pad(mes)}-01`;
  }
  // dd/mm/yyyy o dd-mm-yyyy
  const mNum = n.match(/(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})/);
  if (mNum) {
    const y = mNum[3].length === 2 ? "20" + mNum[3] : mNum[3];
    return `${y}-${pad(mNum[2])}-${pad(mNum[1])}`;
  }
  return null;
};

const tryLocalIntent = (text, pathname) => {
  const isProcesosCajas      = pathname?.startsWith("/procesos/general") && !pathname?.startsWith("/procesos/general_contenidos");
  const isProcesosCont       = pathname?.startsWith("/procesos/general_contenidos");
  const isDigitalizaciones   = pathname?.startsWith("/digitalizacion/admin");
  const isServiciosGuarda    = pathname?.startsWith("/comprobantes/servicios_guarda");
  const isUbicaciones        = pathname?.startsWith("/ubicaciones/admin");

  if (!isProcesosCajas && !isProcesosCont && !isDigitalizaciones && !isServiciosGuarda && !isUbicaciones) return null;

  const norm = normalizeStr(text);

  // ── Servicios de Guarda ───────────────────────────────────────────────────
  if (isServiciosGuarda) {
    // Imprimir comprobante generado
    if (/imprim|ver\s+comprobante|mostrar\s+comprobante/.test(norm)) {
      const resultados = _pageState.serviciosGuardaResultados || [];
      const exitosos = resultados.filter((r) => r.id_comprobante && !r.error);
      if (exitosos.length === 0) {
        return { reply: "No hay comprobantes generados recientemente para imprimir. Generá uno primero." };
      }
      if (exitosos.length === 1) {
        return {
          action: { type: "servicios_guarda_imprimir", params: { id_comprobante: exitosos[0].id_comprobante } },
          reply: `Abriendo el comprobante **#${exitosos[0].id_comprobante}** para **${exitosos[0].cliente}**…`,
        };
      }
      // Múltiples → opciones
      return {
        reply: "¿Cuál comprobante querés imprimir?",
        options: exitosos.map((r) => ({
          label: `#${r.id_comprobante} — ${r.cliente}`,
          action: { type: "servicios_guarda_imprimir", params: { id_comprobante: r.id_comprobante } },
        })),
      };
    }

    // Generar comprobante
    if (/generar?|procesar|comprobante/.test(norm)) {
      // Extraer segmento de clientes: entre "para" y "desde" (o fin)
      const mClientes = norm.match(/para\s+(?:el\s+)?(?:los\s+)?(?:clientes?\s+)?([a-z0-9\s&.,]+?)(?:\s+desde\b|\s*$)/);
      const clientesPart = mClientes ? mClientes[1].trim() : null;

      let clientesResueltos = [];
      let noEncontrados = [];

      if (clientesPart) {
        const nombres = clientesPart.split(/\s+y\s+|,\s*/).map((n) => n.trim()).filter((n) => n.length > 1);
        for (const nombre of nombres) {
          const found = buscarClientePorNombre(nombre);
          if (found.length === 1) clientesResueltos.push(found[0]);
          else if (found.length === 0) noEncontrados.push(nombre);
          else clientesResueltos.push(found[0]); // primera coincidencia si hay varias
        }
      }

      if (noEncontrados.length > 0) {
        return { reply: `No encontré clientes con los nombres: **${noEncontrados.join(", ")}**. Verificá el nombre e intentá de nuevo.` };
      }
      if (clientesResueltos.length === 0) {
        return { reply: "¿Para qué cliente(s) querés generar el comprobante? Indicame el nombre." };
      }

      // Extraer fechas desde el texto original (conserva mayúsculas/tildes)
      const mDesde = text.match(/desde\s+(?:la?\s+)?(?:el\s+)?(.+?)(?:\s+hasta\b|$)/i);
      const mHasta = text.match(/hasta\s+(?:el\s+)?(?:d[ií]a\s+de\s+)?(.+?)(?:\s*$)/i);

      const today = new Date();
      const pad = (x) => String(x).padStart(2, "0");
      const firstDay = `${today.getFullYear()}-${pad(today.getMonth()+1)}-01`;
      const todayStr = `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`;

      const fd = mDesde ? (parseFechaES(mDesde[1].trim()) || firstDay) : firstDay;
      const fh = mHasta ? (parseFechaES(mHasta[1].trim()) || todayStr) : todayStr;

      const nombreClientes = clientesResueltos.map((c) => c.razon_social).join(", ");
      return {
        action: {
          type: "servicios_guarda_generar",
          params: { clientes: clientesResueltos, fecha_desde: fd, fecha_hasta: fh },
        },
        reply: `Generando comprobante para **${nombreClientes}** del período **${fd}** al **${fh}**…`,
      };
    }
    return null;
  }

  // ── Ubicaciones ──────────────────────────────────────────────────────────
  if (isUbicaciones) {
    if (/busca?r?|mostra?r?|ver|consulta?r?|ubicaciones?/.test(norm)) {
      // Estado
      const ESTADOS_UBICACION = [
        { label: "Disponible", keywords: [/disponibles?|libres?/] },
        { label: "Ocupada",    keywords: [/ocupadas?/] },
        { label: "Reservada",  keywords: [/reservadas?/] },
        { label: "En Espera",  keywords: [/en\s+espera/] },
        { label: "Baja",       keywords: [/\bbaja\b/] },
      ];
      const estadosData = lsGetArray("estados_ubicaciones");
      const estadoMatch = ESTADOS_UBICACION.find((e) => e.keywords.some((re) => re.test(norm)));
      let estadoId = null; let estadoLabel = null;
      if (estadoMatch) {
        const found = estadosData.find((e) =>
          normalizeStr(e.detalle || e.nombre || "").includes(normalizeStr(estadoMatch.label))
        );
        if (found) { estadoId = found.id; estadoLabel = found.detalle || found.nombre; }
      }

      // Coordenadas numéricas
      const mSector     = norm.match(/sector\s+(\d+)/);
      const mModulo     = norm.match(/modulo\s+(\d+)/);
      const mEstante    = norm.match(/estante\s+(\d+)/);
      const mPosicion   = norm.match(/posicion\s+(\d+)/);
      const mEstibe     = norm.match(/estibe\s+(\d+)/);
      const mProfund    = norm.match(/profundidad\s+(\d+)/);

      // Depósito
      const depositosData = lsGetArray("owner_localizaciones");
      let depositoId = null; let depositoLabel = null;
      const mDep = norm.match(/deposito\s+([a-z0-9\s]+?)(?:\s+sector|\s+modulo|\s+estante|\s+con|\s*$)/);

      if (mDep) {
        const nombreBuscado = mDep[1].trim();
        const found = depositosData.filter((d) =>
          normalizeStr(d.nombre || d.detalle || d.label || "").includes(nombreBuscado) ||
          String(d.id) === nombreBuscado
        );
        if (found.length === 0) {
          return { reply: `No encontré el depósito **"${nombreBuscado}"**. Verificá el nombre.` };
        }
        if (found.length === 1) {
          depositoId = found[0].id;
          depositoLabel = found[0].nombre || found[0].detalle || found[0].label;
        } else {
          return {
            reply: "Encontré varios depósitos. ¿Cuál querés usar?",
            options: found.slice(0, 6).map((d) => ({
              label: d.nombre || d.detalle || d.label || String(d.id),
              action: { type: "buscar_ubicaciones", params: {
                id_deposito: d.id,
                id_estado: estadoId,
                sector:     mSector?.[1] || "",
                modulo:     mModulo?.[1] || "",
                estante:    mEstante?.[1] || "",
              }},
            })),
          };
        }
      } else if (depositosData.length === 1) {
        // Un solo depósito → usarlo automáticamente
        depositoId = depositosData[0].id;
        depositoLabel = depositosData[0].nombre || depositosData[0].detalle || depositosData[0].label;
      } else if (depositosData.length > 1) {
        // Varios depósitos → preguntar
        return {
          reply: "¿En qué depósito querés buscar?",
          options: depositosData.slice(0, 6).map((d) => ({
            label: d.nombre || d.detalle || d.label || String(d.id),
            action: { type: "buscar_ubicaciones", params: {
              id_deposito: d.id,
              id_estado: estadoId,
              sector:     mSector?.[1] || "",
              modulo:     mModulo?.[1] || "",
              estante:    mEstante?.[1] || "",
            }},
          })),
        };
      }

      const params = {
        id_deposito: depositoId,
        sector:      mSector?.[1] || "",
        modulo:      mModulo?.[1] || "",
        estante:     mEstante?.[1] || "",
        posicion:    mPosicion?.[1] || "",
        estibe:      mEstibe?.[1] || "",
        profundidad: mProfund?.[1] || "",
        id_estado:   estadoId,
      };

      const partes = [];
      if (depositoLabel) partes.push(`depósito **${depositoLabel}**`);
      if (estadoLabel)   partes.push(`estado **${estadoLabel}**`);
      if (params.sector)  partes.push(`sector **${params.sector}**`);
      if (params.modulo)  partes.push(`módulo **${params.modulo}**`);
      if (params.estante) partes.push(`estante **${params.estante}**`);

      return {
        action: { type: "buscar_ubicaciones", params },
        reply: `Buscando ubicaciones${partes.length ? " — " + partes.join(", ") : ""}…`,
      };
    }
    return null;
  }

  const PROCESOS = isProcesosCont ? PROCESOS_CONTENIDOS : PROCESOS_CAJAS;
  const searchAction = isProcesosCont
    ? "search_contenidos"
    : isDigitalizaciones
      ? "search_digitalizaciones"
      : "search_cajas";
  const entityLabel = isProcesosCont ? "contenido" : "caja";

  const subProcesos = _pageState.subProcesos || [];

  // ── Mostrar opciones de subproceso (solo en rutas de procesos) ───────────
  if (!isDigitalizaciones && /subproceso|sub.proceso|opciones|que opciones|que subprocesos|ver subprocesos/.test(norm)) {
    if (subProcesos.length === 0) {
      return { reply: "Primero seleccioná un proceso para ver las opciones de subproceso." };
    }
    const options = subProcesos.map((sp) => ({
      label: `${sp.label} ${sp.estado}`,
      action: { type: "select_subproceso", params: { label: sp.label } },
    }));
    return {
      reply: "Estos son los subprocesos disponibles. Hacé clic para seleccionar uno:",
      options,
    };
  }

  // ── Seleccionar subproceso por número o nombre ────────────────────────────
  const matchSpNum = norm.match(/subproceso\s+(?:n[uú]mero\s+)?(\d+)|opcion\s+(\d+)/);
  if (matchSpNum) {
    const idx = parseInt(matchSpNum[1] || matchSpNum[2], 10) - 1;
    const sp = subProcesos[idx];
    if (sp) {
      return {
        action: { type: "select_subproceso", params: { label: sp.label } },
        reply: `Seleccioné el subproceso **${sp.label}**.`,
      };
    }
  }

  // ── Cambio de proceso (solo en rutas de procesos) ────────────────────────
  if (!isDigitalizaciones) {
    for (const proc of PROCESOS) {
      if (proc.patterns.some((re) => re.test(norm))) {
        return {
          action: { type: "select_proceso", params: { proceso_id: proc.id } },
          reply: `Listo, cambié al proceso **${proc.label}**.`,
        };
      }
    }
  }

  // ── Buscar por ID ─────────────────────────────────────────────────────────
  const matchId = norm.match(/(?:busca?r?|mostrar?)\s+(?:(?:las?\s+)?(?:cajas?|contenidos?)\s+)?(?:numeros?\s+)?(\d[\d,\s]*(?:(?:\s*[y,]\s*)\d+)*)/);
  if (matchId && /\d/.test(matchId[1])) {
    const ids = matchId[1]
      .replace(/\s*[y,]\s*/g, ",")
      .replace(/\s+/g, "")
      .replace(/,+/g, ",")
      .replace(/,$/, "");
    const plural = ids.includes(",");
    return {
      action: { type: searchAction, params: { id_cajas: ids } },
      reply: `Buscando ${plural ? `los ${entityLabel}s` : `el ${entityLabel}`} **${ids}**…`,
    };
  }

  // ── Buscar por referencia ─────────────────────────────────────────────────
  const matchRef = norm.match(/(?:busca?r?|referencia)\s+(?:referencia|ref\.?)?\s*(\S+)/);
  if (matchRef && /ref/.test(norm)) {
    return {
      action: { type: searchAction, params: { referencia: matchRef[1] } },
      reply: `Buscando por referencia **${matchRef[1]}**…`,
    };
  }

  // ── Buscar por estado ─────────────────────────────────────────────────────
  const ESTADOS = [
    { id: "4",  detalle: "Creada",                         keywords: [/\bcreadas?\b/] },
    { id: "5",  detalle: "Sin Ubicación",                  keywords: [/sin\s+ubicacion/] },
    { id: "6",  detalle: "Alojada",                        keywords: [/\balojadas?\b(?!\s+temp)/] },
    { id: "7",  detalle: "Alojada Temporalmente",          keywords: [/alojadas?\s+temp/, /alojamiento\s+temp/] },
    { id: "10", detalle: "En Cliente",                     keywords: [/en\s+cliente(?!\s+sin)(?!\s+con)/] },
    { id: "11", detalle: "Esperando devolución",           keywords: [/esperando\s+devolucion/, /devolucion\s+pendiente/, /\bpendiente\s+devolucion/] },
    { id: "16", detalle: "En cliente sin ubicación",       keywords: [/en\s+cliente\s+sin\s+ubic/] },
    { id: "17", detalle: "En cliente con ubicación pend.", keywords: [/en\s+cliente\s+con\s+ubic/] },
  ];

  if (/estado|alojad|en\s+cliente|sin\s+ubic|creadas?|esperando/.test(norm)) {
    const encontrados = ESTADOS.filter((e) => e.keywords.some((re) => re.test(norm)));
    if (encontrados.length > 0) {
      const labels = encontrados.map((e) => e.detalle).join(", ");
      return {
        action: { type: searchAction, params: { estados: encontrados } },
        reply: `Buscando cajas con estado **${labels}**…`,
      };
    }
  }

  // ── Buscar por fecha ──────────────────────────────────────────────────────
  const parseFecha = (s) => {
    // dd/mm/yyyy o dd-mm-yyyy
    const m = s.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (m) {
      const y = m[3].length === 2 ? "20" + m[3] : m[3];
      return `${y}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}`;
    }
    return null;
  };

  if (/desde|hasta|fecha|creadas?\s+en/.test(norm)) {
    const mDesde = text.match(/desde\s+(?:el\s+)?(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i);
    const mHasta = text.match(/hasta\s+(?:el\s+)?(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i);
    const fd = mDesde ? parseFecha(mDesde[1]) : null;
    const fh = mHasta ? parseFecha(mHasta[1]) : null;
    if (fd || fh) {
      const partes = [fd && `desde **${mDesde[1]}**`, fh && `hasta **${mHasta[1]}**`].filter(Boolean).join(" ");
      return {
        action: { type: searchAction, params: { fecha_desde: fd || "", fecha_hasta: fh || "" } },
        reply: `Buscando cajas creadas ${partes}…`,
      };
    }
  }

  // ── Buscar por cliente ────────────────────────────────────────────────────
  if (/cliente|cuenta/.test(norm) && /busca?r?|cajas?|mostra?r?|de\s/.test(norm)) {
    // Extraer el nombre del cliente del texto
    const matchNombre =
      norm.match(/(?:del?\s+)?cliente\s+([a-z0-9\s&.]+?)(?:\s*$)/) ||
      norm.match(/cajas?\s+(?:de(?:l)?)\s+([a-z0-9\s&.]+?)(?:\s*$)/);

    if (matchNombre) {
      const nombreBuscado = matchNombre[1].trim();
      const clientesEncontrados = buscarClientePorNombre(nombreBuscado);

      if (clientesEncontrados.length === 0) {
        return { reply: `No encontré ningún cliente con el nombre **${nombreBuscado.toUpperCase()}**. Verificá el nombre e intentá de nuevo.` };
      }
      if (clientesEncontrados.length === 1) {
        const c = clientesEncontrados[0];
        return {
          action: { type: searchAction, params: { id_cuenta: c.id_cuentas, razon_social: c.razon_social } },
          reply: `Buscando cajas del cliente **${c.razon_social}**…`,
        };
      }
      // Múltiples coincidencias → mostrar opciones
      return {
        reply: `Encontré varios clientes. ¿Cuál querés?`,
        options: clientesEncontrados.slice(0, 6).map((c) => ({
          label: c.razon_social,
          action: { type: searchAction, params: { id_cuenta: c.id_cuentas, razon_social: c.razon_social } },
        })),
      };
    }
  }

  // ── Buscar por ubicación (requiere abrir buscador) ────────────────────────
  if (/ubicacion|deposito|estante|nivel|posicion/.test(norm) && /busca?r?|cajas?|mostra?r?/.test(norm)) {
    return {
      action: { type: "open_search" },
      reply: "Para buscar por ubicación seleccioná los filtros en el buscador (pestaña **Ubicaciones**). Te lo abro:",
    };
  }

  // ── Crear caja ────────────────────────────────────────────────────────────
  if (/crear?\s+(?:una\s+)?caja|nueva\s+caja|agregar\s+caja/.test(norm)) {
    return {
      action: { type: "open_create_caja" },
      reply: "Abrí el formulario para crear una nueva caja.",
    };
  }

  // ── Abrir búsqueda avanzada ───────────────────────────────────────────────
  if (/abrir?\s+(?:la\s+)?busqueda|filtro|busqueda\s+avanzada/.test(norm)) {
    return {
      action: { type: "open_search" },
      reply: "Abrí el panel de búsqueda avanzada.",
    };
  }

  return null;
};

/* ─── Hook de lógica de chat ──────────────────────────────────────────────── */
const useChatLogic = (initialMessage, pathname) => {
  const [messages,  setMessages]  = useState([initialMessage]);
  const [input,     setInput]     = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);
  const readerRef = useRef(null);

  // Actualizar mensaje inicial si cambia de ruta y no hay conversación en curso
  const resetWithMessage = useCallback((msg) => {
    if (readerRef.current) { readerRef.current.cancel(); readerRef.current = null; }
    setStreaming(false);
    setMessages([msg]);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;

    const userMsg = { role: "user", content: sanitize(text) };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");

    // Intent local: si coincide, ejecuta la acción y responde sin llamar al backend
    const intent = tryLocalIntent(text, pathname);
    if (intent) {
      if (intent.action) {
        window.dispatchEvent(new CustomEvent("ai:action", { detail: intent.action }));
      }
      setMessages([...history, {
        role: "assistant",
        content: intent.reply,
        options: intent.options || [],
      }]);
      return;
    }
    setStreaming(true);

    const assistantIdx = history.length;
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    try {
      const clientData = JSON.parse(localStorage.getItem("clientData"));
      const dominio    = (clientData?.dominio || "").replace(/\/+$/, "");
      const userData   = JSON.parse(localStorage.getItem("siam-user-data"));

      const response = await fetch(`${dominio}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("authToken") || "",
        },
        body: JSON.stringify({
          messages: history.slice(-MAX_HISTORY),
          context: {
            owner_code: clientData?.owner_code || "",
            id_usuario: userData?.id,
          },
        }),
      });

      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const reader  = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let lineBuffer  = "";
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer  = lines.pop();

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data: ")) continue;
          const payload = trimmed.slice(6);
          if (payload === "[DONE]") break;
          try {
            const parsed = JSON.parse(payload);

            if (parsed.error) {
              accumulated = parsed.error;
              setMessages((prev) => {
                const next = [...prev];
                next[assistantIdx] = { role: "assistant", content: accumulated };
                return next;
              });
              break;
            }

            if (parsed._strip !== undefined) {
              const strip = parsed._strip;
              accumulated = accumulated.replace(strip, "").trimEnd();
              setMessages((prev) => {
                const next = [...prev];
                next[assistantIdx] = { role: "assistant", content: accumulated, actions: parsed.actions || [] };
                return next;
              });
              // Disparar acciones de UI si el backend las envía
              if (parsed.uiActions?.length) {
                parsed.uiActions.forEach((action) => {
                  window.dispatchEvent(new CustomEvent("ai:action", { detail: action }));
                });
              }
              break;
            }

            if (parsed.token) {
              accumulated += parsed.token;
              setMessages((prev) => {
                const next = [...prev];
                next[assistantIdx] = { role: "assistant", content: accumulated };
                return next;
              });
            }
          } catch { /* chunk malformado */ }
        }
      }

      if (!accumulated) {
        setMessages((prev) => {
          const next = [...prev];
          next[assistantIdx] = { role: "assistant", content: "No se recibió respuesta. Intentá nuevamente." };
          return next;
        });
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: "assistant",
          content: "Hubo un error al conectar con el asistente. Verificá tu conexión e intentá nuevamente.",
        };
        return next;
      });
    } finally {
      readerRef.current = null;
      setStreaming(false);
    }
  };

  const handleClear = useCallback((newInitial) => {
    if (readerRef.current) { readerRef.current.cancel(); readerRef.current = null; }
    setStreaming(false);
    setMessages([newInitial || { role: "assistant", content: "Conversación reiniciada. ¿En qué te puedo ayudar?" }]);
  }, []);

  return { messages, input, setInput, streaming, bottomRef, inputRef, handleSend, handleClear, resetWithMessage };
};

/* ─── Burbuja de aviso contextual ────────────────────────────────────────── */
const HINT_DISMISS_KEY = "ai-hint-dismissed";

const ContextHintBubble = ({ hint, onOpen, onDismiss, mode }) => {
  const theme = useTheme();

  const isFloating = mode === "floating";

  return (
    <Grow in timeout={350}>
      <Paper
        elevation={6}
        onClick={onOpen}
        sx={{
          position: "fixed",
          zIndex: 1298,
          cursor: "pointer",
          borderRadius: 2.5,
          px: 1.75,
          py: 1.25,
          maxWidth: 230,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          background: theme.palette.background.paper,
          boxShadow: `0 4px 18px ${alpha(theme.palette.primary.main, 0.22)}`,
          // Posición: arriba del FAB (floating) o al lado del tab
          ...(isFloating
            ? { bottom: 76, right: 76 }
            : { top: "calc(40% - 65px - 8px)", right: TAB_WIDTH + 8 }),
          "&:hover": {
            boxShadow: `0 6px 22px ${alpha(theme.palette.primary.main, 0.32)}`,
          },
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          sx={{
            position: "absolute", top: 4, right: 4,
            color: theme.palette.text.disabled, p: 0.25,
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, fontSize: 18, mt: 0.25, flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" fontWeight={700} color="primary" display="block" lineHeight={1.3}>
              ¡Puedo ayudarte aquí!
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.4} mt={0.25}>
              {hint}
            </Typography>
            <Typography variant="caption" color="primary" fontWeight={600} display="block" mt={0.5}>
              Consultame →
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Grow>
  );
};

/* ─── Hook para la burbuja de aviso ─────────────────────────────────────── */
const useContextHint = (pathname, chatOpen) => {
  const [showHint, setShowHint] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // No mostrar si el chat ya está abierto o si no hay hint para esta ruta
    if (chatOpen) { setShowHint(false); return; }

    const hint = getContextHint(pathname);
    if (!hint) { setShowHint(false); return; }

    // Mostrar burbuja con pequeño delay para que no aparezca instantáneo
    const t = setTimeout(() => setShowHint(true), 800);
    timerRef.current = t;

    // Auto-cerrar a los 8 segundos
    const auto = setTimeout(() => setShowHint(false), 8800);

    return () => { clearTimeout(t); clearTimeout(auto); };
  }, [pathname, chatOpen]);

  const dismiss = useCallback(() => setShowHint(false), []);

  return { showHint, dismiss };
};

/* ─── Modo flotante ──────────────────────────────────────────────────────── */
// Escucha eventos ai:state de cualquier página y actualiza el estado global
const usePageStateSync = () => {
  useEffect(() => {
    const handler = (e) => { _pageState = { ..._pageState, ...e.detail }; };
    window.addEventListener("ai:state", handler);
    return () => window.removeEventListener("ai:state", handler);
  }, []);
};

const FloatingMode = () => {
  usePageStateSync();
  const theme    = useTheme();
  const history  = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const initialMsg = buildInitialMessage(location.pathname);
  const { messages, input, setInput, streaming, bottomRef, inputRef, handleSend, handleClear, resetWithMessage } =
    useChatLogic(initialMsg, location.pathname);

  // Cuando cambia de módulo y el chat está cerrado → actualizar saludo
  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (!open && location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      resetWithMessage(buildInitialMessage(location.pathname));
    }
  }, [location.pathname, open, resetWithMessage]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, inputRef]);

  const handleNavigate = (path) => { history.push(path); setOpen(false); };
  const handleSuggestion = (text) => handleSend(text);
  const handleClearWithGreeting = () => handleClear(buildInitialMessage(location.pathname));

  const { showHint, dismiss } = useContextHint(location.pathname, open);

  const handleOpenFromHint = () => { dismiss(); setOpen(true); };

  return (
    <>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper elevation={8} sx={{
          position: "fixed", bottom: 76, right: 20,
          width: PANEL_WIDTH, height: PANEL_HEIGHT,
          display: "flex", flexDirection: "column",
          borderRadius: 3, overflow: "hidden", zIndex: 1299,
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
        }}>
          <ChatPanel
            messages={messages} streaming={streaming}
            input={input} setInput={setInput}
            onSend={() => handleSend()} onClear={handleClearWithGreeting}
            onClose={() => setOpen(false)} onNavigate={handleNavigate}
            onSuggestionSelect={handleSuggestion}
            inputRef={inputRef} bottomRef={bottomRef} showClose
          />
        </Paper>
      </Slide>

      {showHint && (
        <ContextHintBubble
          hint={getContextHint(location.pathname)}
          onOpen={handleOpenFromHint}
          onDismiss={dismiss}
          mode="floating"
        />
      )}

      <Tooltip title={open ? "Cerrar asistente" : "Asistente IA"} placement="left">
        <Fab color="primary" size="medium" onClick={() => { setOpen((p) => !p); dismiss(); }} sx={{
          position: "fixed", bottom: 20, right: 20, zIndex: 1300,
          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.5)}`,
          transition: "transform 0.2s",
          transform: open ? "rotate(20deg) scale(0.92)" : "rotate(0deg) scale(1)",
        }}>
          <SmartToyIcon />
        </Fab>
      </Tooltip>
    </>
  );
};

/* ─── Modo pestaña ───────────────────────────────────────────────────────── */
const TabMode = () => {
  usePageStateSync();
  const theme    = useTheme();
  const history  = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const initialMsg = buildInitialMessage(location.pathname);
  const { messages, input, setInput, streaming, bottomRef, inputRef, handleSend, handleClear, resetWithMessage } =
    useChatLogic(initialMsg, location.pathname);

  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (!open && location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      resetWithMessage(buildInitialMessage(location.pathname));
    }
  }, [location.pathname, open, resetWithMessage]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, inputRef]);

  const handleNavigate    = (path) => { history.push(path); setOpen(false); };
  const handleSuggestion  = (text) => handleSend(text);
  const handleClearWithGreeting = () => handleClear(buildInitialMessage(location.pathname));

  const { showHint, dismiss } = useContextHint(location.pathname, open);

  const handleOpenFromHint = () => { dismiss(); setOpen(true); };

  const tabBg = `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`;

  return (
    <>
    {showHint && (
      <ContextHintBubble
        hint={getContextHint(location.pathname)}
        onOpen={handleOpenFromHint}
        onDismiss={dismiss}
        mode="tab"
      />
    )}
    <Box sx={{
      position: "fixed", top: "40%", transform: "translateY(-50%)",
      right: 0, zIndex: 1299, display: "flex", alignItems: "flex-start",
    }}>
      <Collapse in={open} orientation="horizontal" unmountOnExit>
        <Paper elevation={8} sx={{
          width: PANEL_WIDTH, height: PANEL_HEIGHT,
          display: "flex", flexDirection: "column", overflow: "hidden",
          borderRadius: "12px 0 0 12px",
          border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          borderRight: "none",
        }}>
          <ChatPanel
            messages={messages} streaming={streaming}
            input={input} setInput={setInput}
            onSend={() => handleSend()} onClear={handleClearWithGreeting}
            onClose={() => setOpen(false)} onNavigate={handleNavigate}
            onSuggestionSelect={handleSuggestion}
            inputRef={inputRef} bottomRef={bottomRef} showClose={false}
          />
        </Paper>
      </Collapse>

      <Tooltip title={open ? "Cerrar asistente" : "Asistente IA"} placement="left">
        <Box onClick={() => { setOpen((p) => !p); dismiss(); }} sx={{
          background: tabBg, width: TAB_WIDTH, minHeight: 130,
          borderRadius: "8px 0 0 8px", cursor: "pointer",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: 0.75, py: 1.5,
          boxShadow: `-3px 0 12px ${alpha(theme.palette.primary.main, 0.35)}`,
          transition: "width 0.15s, box-shadow 0.2s",
          "&:hover": {
            width: TAB_WIDTH + 4,
            boxShadow: `-4px 0 16px ${alpha(theme.palette.primary.main, 0.5)}`,
          },
          userSelect: "none",
        }}>
          <SmartToyIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography variant="caption" sx={{
            color: "#fff", fontWeight: 700, fontSize: "0.65rem",
            writingMode: "vertical-rl", textOrientation: "mixed",
            transform: "rotate(180deg)", letterSpacing: "0.06em",
            lineHeight: 1.2, textAlign: "center",
          }}>
            Asistente IA
          </Typography>
          <KeyboardArrowRightIcon sx={{
            color: "rgba(255,255,255,0.7)", fontSize: 16,
            transition: "transform 0.2s",
            transform: open ? "rotate(0deg)" : "rotate(180deg)",
          }} />
        </Box>
      </Tooltip>
    </Box>
    </>
  );
};

/* ─── Componente principal ───────────────────────────────────────────────── */
export const AIChatWidget = ({ enabled = true, mode = "floating" }) => {
  if (!enabled) return null;
  return mode === "tab" ? <TabMode /> : <FloatingMode />;
};

export default AIChatWidget;
